/**
 * Edge job helpers for the HALO Device Console (Phase 2).
 * Enqueues http_request jobs on the Virtual Edge installation and polls to completion.
 */
import { HTTPClient } from '@/assets/js/http';

const client = new HTTPClient();

/**
 * Whether an http_request job result looks successful.
 * @param {Object} job EdgeJob view
 * @returns {boolean}
 */
export function jobSucceeded(job) {
	if (!job || job.status !== 'completed') return false;
	const result = job.result || {};
	if (result.success === false) return false;
	const data = result.data || {};
	if (typeof data.ok === 'boolean') return data.ok;
	const status = Number(data.status);
	if (Number.isFinite(status)) return status >= 200 && status < 400;
	// Agent reported job success with no HTTP status wrapper.
	if (result.success === true) return true;
	return false;
}

/**
 * Human-readable failure from a completed/failed EdgeJob.
 * @param {Object} job
 * @returns {string}
 */
export function jobErrorMessage(job) {
	const result = (job && job.result) || {};
	const data = result.data || {};
	if (result.error && result.error.message) return result.error.message;
	if (typeof data.error === 'string' && data.error) return data.error;
	const status = Number(data.status);
	if (Number.isFinite(status) && (status < 200 || status >= 400)) {
		return `Halo HTTP ${status}`;
	}
	return `Halo request failed (${(job && job.status) || 'unknown'})`;
}

/**
 * Extract response body from a completed http_request job.
 * @param {Object} job
 * @returns {*}
 */
export function jobBody(job) {
	const data = (job && job.result && job.result.data) || {};
	if (data.data !== undefined) return data.data;
	if (data.body !== undefined) {
		if (typeof data.body === 'string') {
			try {
				return JSON.parse(data.body);
			} catch (e) {
				return data.body;
			}
		}
		return data.body;
	}
	return data;
}

/**
 * Poll a single EdgeJob until terminal status.
 * @param {string} installationId
 * @param {string} jobId
 * @param {number} [timeoutMs=15000]
 * @returns {Promise<Object>}
 */
export async function pollEdgeJob(installationId, jobId, timeoutMs = 15000) {
	const deadline = Date.now() + Math.max(20000, Number(timeoutMs) + 15000);
	let delay = 300;
	while (Date.now() < deadline) {
		await new Promise((resolve) => setTimeout(resolve, delay));
		delay = Math.min(800, delay + 100);
		try {
			const res = await client.robustQuery(
				`/api/edge/admin/installations/${installationId}/jobs/${jobId}`
			);
			const job = res.results || {};
			const status = job.status;
			if (status === 'completed' || status === 'failed' || status === 'cancelled') {
				return job;
			}
			continue;
		} catch (e) {
			// Older servers may lack single-job GET — fall back to list.
			const list = await client.robustQuery(
				`/api/edge/admin/installations/${installationId}/jobs`
			);
			const jobs = list.results || [];
			const job = jobs.find((j) => (j.jobId || j.id) === jobId);
			if (job && (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled')) {
				return job;
			}
		}
	}
	throw new Error('Timed out waiting for the Virtual Edge agent');
}

/**
 * Enqueue http_request via admin device helper (merges httpDevices auth server-side).
 * @param {string} installationId
 * @param {Object} edgeDevice
 * @param {{ path: string, method?: string, body?: *, timeoutMs?: number }} overrides
 * @returns {Promise<*>} Parsed device response body
 */
export async function runHaloHttp(installationId, edgeDevice, overrides) {
	if (!installationId) throw new Error('Missing Virtual Edge installation');
	if (!edgeDevice) throw new Error('Missing edge device');
	const deviceId = edgeDevice.id;
	if (!deviceId) throw new Error('Edge device id is required');
	if (!overrides || !overrides.path) throw new Error('path is required');

	const timeoutMs = overrides.timeoutMs != null ? Number(overrides.timeoutMs) : 15000;
	const created = await client.robustPost(
		`/api/edge/admin/installations/${installationId}/devices/${deviceId}/http`,
		{
			path: overrides.path,
			method: (overrides.method || 'GET').toUpperCase(),
			body: overrides.body,
			timeoutMs,
			headers: overrides.headers,
		}
	);
	const jobId = (created.results && (created.results.jobId || created.results.id)) || null;
	if (!jobId) throw new Error('Edge job was not created');

	const job = await pollEdgeJob(installationId, jobId, timeoutMs);
	if (!jobSucceeded(job)) {
		throw new Error(jobErrorMessage(job));
	}
	return jobBody(job);
}

/**
 * Persist Halo console read-only metadata on an EdgeDevice.
 * @param {string} installationId
 * @param {string} deviceId
 * @param {boolean} readOnlyMode
 * @returns {Promise<Object>} Updated device view
 */
export async function patchHaloReadOnlyMode(installationId, deviceId, readOnlyMode) {
	if (!installationId) throw new Error('Missing Virtual Edge installation');
	if (!deviceId) throw new Error('Edge device id is required');
	const res = await client.robustPatch(
		`/api/edge/admin/installations/${installationId}/devices/${deviceId}`,
		{ readOnlyMode: !!readOnlyMode }
	);
	return res.results || res;
}

/**
 * Write then read-back the same path (confirm-on-device).
 * @param {string} installationId
 * @param {Object} edgeDevice
 * @param {string} path
 * @param {*} body
 * @returns {Promise<*>} Read-back body
 */
export async function putAndConfirm(installationId, edgeDevice, path, body) {
	await runHaloHttp(installationId, edgeDevice, {
		path,
		method: 'PUT',
		body,
		timeoutMs: 20000,
	});
	await new Promise((resolve) => setTimeout(resolve, 400));
	return runHaloHttp(installationId, edgeDevice, {
		path,
		method: 'GET',
		timeoutMs: 15000,
	});
}
